import React from "react";
import Link from "next/link";

import { Icon } from "../../icons";
import {
  SessionDetailsContainer,
  TitleContainer,
  DetailsContainer,
  ShowNameTypography,
  SessionNameTypography,
  InfoTypography,
  InfoLinkTypography,
  InfoItem,
  PriorityTypography,
} from "./SessionDetails.styles";
import { TicketTypeVariants } from "./SessionDetails.variants";
import { SessionDetailsInterface } from "../../models/Session";
import { useTheme } from "@mui/material";
import { BookingInterestAllocationInterface, BookingInterestAllocationSessionInterface, UserBookingInterest } from "src/models/BookingInterest";


type TicketVariantsObjType = ReturnType<typeof TicketTypeVariants>;
export type TicketVariantsType = keyof TicketVariantsObjType;
export interface CustomSessionDetailsProps {
  showName?: string | null;
  showMinimumAge?: number | null;
  sessionDetails?: SessionDetailsInterface | null;
  ticketType?: TicketVariantsType;
  allocation?: BookingInterestAllocationInterface;
  relatedBookingInterests?: BookingInterestAllocationSessionInterface;
  bookingInterest?: UserBookingInterest;
  isPriority?: boolean
}

export const SessionDetails = ({
  showName = null,
  sessionDetails = null,
  showMinimumAge,
  ticketType = undefined,
  allocation,
  relatedBookingInterests,
  bookingInterest = undefined,
  isPriority = false,
  ...rest
}: CustomSessionDetailsProps) => {
  const minimumAgeRequirement = sessionDetails?.minimumAgeRequirement ?? (showMinimumAge === 0 ? null : showMinimumAge);
  const theme = useTheme();

  const ticketTypeStyles = TicketTypeVariants(theme);

  let ticketTypeText: string;
  switch (ticketType) {
    case "generalAdmission":
      ticketTypeText = "General Admission";
      break;
    case "accessibilityRequired":
      ticketTypeText = "Accessibility Required";
      break;
    case "vip":
      ticketTypeText = "VIP";
      break;
    case "friendsAndFamily":
      ticketTypeText = "Friends & Family";
      break;
    case "priority":
      ticketTypeText = "Priority";
      break;
    case "priorityAccessibility":
      ticketTypeText = "Priority Entry\u2728 \u00B7 Accessibility Required";
      break;
    default:
      ticketTypeText = "General Admission";
  }

  return (
    <SessionDetailsContainer>
      <TitleContainer>
        <ShowNameTypography data-cy="sessionDetails-showName">{showName}</ShowNameTypography>
        <SessionNameTypography data-cy="sessionDetails-sessionName">{sessionDetails?.name}</SessionNameTypography>
        {isPriority && (
          <PriorityTypography>
            Priority Entry✨
          </PriorityTypography>
        )}
      </TitleContainer>

      <DetailsContainer isPriority={isPriority}>
        {allocation && (
          <>
            <InfoItem>
              <Icon icon="Ticket" sx={{ ...ticketTypeStyles[allocation.type?.toLocaleLowerCase() as keyof TicketVariantsObjType] }} />
              <InfoTypography data-cy="sessionDetails-date">{allocation.type}</InfoTypography>
            </InfoItem>
            <InfoItem>
              <Icon icon="Ticket" sx={{ ...ticketTypeStyles[allocation.type?.toLocaleLowerCase() as keyof TicketVariantsObjType] }} />
              <InfoTypography data-cy="sessionDetails-date">{allocation.name}</InfoTypography>
            </InfoItem>
          </>
        )}
        {ticketType && (
          <InfoItem>
            <Icon icon="Ticket" sx={{ ...ticketTypeStyles[ticketType] }} />
            <InfoTypography>{ticketTypeText}</InfoTypography>
          </InfoItem>
        )}
        {bookingInterest && bookingInterest?.lastMinute &&
          <InfoItem>
            <Icon icon="Ticket" />
            <InfoTypography>Last Minute</InfoTypography>
          </InfoItem>
        }
        <InfoItem>
          <Icon icon="Calendar" />
          <InfoTypography data-cy="sessionDetails-date">{sessionDetails?.date} (Local time)</InfoTypography>
        </InfoItem>
        <InfoItem>
          <Icon icon="Clock" />
          <InfoTypography>
            Arrival: {sessionDetails?.arrivalTime} - Finish:{" "}
            {sessionDetails?.approxFinishTime} (approx)
          </InfoTypography>
        </InfoItem>
        {sessionDetails?.location && (
          <InfoItem>
            <Icon icon="LocationMarker" />
            <InfoLinkTypography component="span">
              <Link
                data-cy="session-location-name"
                href={sessionDetails?.location?.link}
                target="_blank"
                onClick={(e) => e.stopPropagation()}
              >
                {sessionDetails?.location?.name}
              </Link>
            </InfoLinkTypography>
          </InfoItem>
        )}
        {minimumAgeRequirement && (
          <InfoItem>
            <Icon icon="Cake" />
            <InfoTypography>
              Min Age: {minimumAgeRequirement}
            </InfoTypography>
          </InfoItem>
        )}
      </DetailsContainer>
    </SessionDetailsContainer>
  );
};

export interface SessionDetailsProps
  extends React.ComponentProps<typeof SessionDetails> {
  as?: React.ElementType;
}

SessionDetails.displayName = "SessionDetails";
